interface CountryCurrencyMap {
  [country: string]: {
    currency: string;
    name: string;
  };
}

// Map of countries to their currencies
export const countryCurrencyMap: CountryCurrencyMap = {
  "United States": { currency: "USD", name: "US Dollar" },
  "France": { currency: "EUR", name: "Euro" },
  "Germany": { currency: "EUR", name: "Euro" },
  "Italy": { currency: "EUR", name: "Euro" },
  "Spain": { currency: "EUR", name: "Euro" },
  "United Kingdom": { currency: "GBP", name: "British Pound" },
  "Japan": { currency: "JPY", name: "Japanese Yen" },
  "Australia": { currency: "AUD", name: "Australian Dollar" },
  "Canada": { currency: "CAD", name: "Canadian Dollar" },
  "China": { currency: "CNY", name: "Chinese Yuan" },
  "India": { currency: "INR", name: "Indian Rupee" },
  "Brazil": { currency: "BRL", name: "Brazilian Real" },
  "Russia": { currency: "RUB", name: "Russian Ruble" },
  "South Korea": { currency: "KRW", name: "South Korean Won" },
  "Switzerland": { currency: "CHF", name: "Swiss Franc" },
  "Singapore": { currency: "SGD", name: "Singapore Dollar" },
  "Sweden": { currency: "SEK", name: "Swedish Krona" },
  "Norway": { currency: "NOK", name: "Norwegian Krone" },
  "Denmark": { currency: "DKK", name: "Danish Krone" },
  "Poland": { currency: "PLN", name: "Polish Złoty" },
  "Mexico": { currency: "MXN", name: "Mexican Peso" },
  "South Africa": { currency: "ZAR", name: "South African Rand" },
  "Turkey": { currency: "TRY", name: "Turkish Lira" },
  "Indonesia": { currency: "IDR", name: "Indonesian Rupiah" },
  "Thailand": { currency: "THB", name: "Thai Baht" },
  "Malaysia": { currency: "MYR", name: "Malaysian Ringgit" },
  "Philippines": { currency: "PHP", name: "Philippine Peso" },
  "New Zealand": { currency: "NZD", name: "New Zealand Dollar" },
  "Hong Kong": { currency: "HKD", name: "Hong Kong Dollar" },
  "United Arab Emirates": { currency: "AED", name: "UAE Dirham" },
};

// Common European Union countries using the Euro
const euCountries = [
  "Austria", "Belgium", "Cyprus", "Estonia", "Finland", "France", "Germany", 
  "Greece", "Ireland", "Italy", "Latvia", "Lithuania", "Luxembourg", "Malta", 
  "Netherlands", "Portugal", "Slovakia", "Slovenia", "Spain"
];

// Add all EU countries to the map
euCountries.forEach(country => {
  if (!countryCurrencyMap[country]) {
    countryCurrencyMap[country] = { currency: "EUR", name: "Euro" };
  }
});

// Simple mapping of major cities to their countries
const cityToCountryMap: Record<string, string> = {
  "New York": "United States",
  "Los Angeles": "United States",
  "Chicago": "United States",
  "London": "United Kingdom",
  "Paris": "France",
  "Berlin": "Germany",
  "Madrid": "Spain",
  "Rome": "Italy",
  "Tokyo": "Japan",
  "Sydney": "Australia",
  "Toronto": "Canada",
  "Beijing": "China",
  "Delhi": "India",
  "Mumbai": "India",
  "Sao Paulo": "Brazil",
  "Moscow": "Russia",
  "Seoul": "South Korea",
  "Zurich": "Switzerland",
  "Singapore": "Singapore",
  "Stockholm": "Sweden",
  "Oslo": "Norway",
  "Copenhagen": "Denmark",
  "Warsaw": "Poland",
  "Mexico City": "Mexico",
  "Johannesburg": "South Africa",
  "Istanbul": "Turkey",
  "Jakarta": "Indonesia",
  "Bangkok": "Thailand",
  "Kuala Lumpur": "Malaysia",
  "Manila": "Philippines",
  "Auckland": "New Zealand",
  "Hong Kong": "Hong Kong",
  "Dubai": "United Arab Emirates",
};

// Function to get the currency code from a city name
export const getCurrencyFromCity = async (cityName: string): Promise<string> => {
  console.debug("🔍 getCurrencyFromCity: Starting currency lookup for city:", cityName);
  
  if (!cityName) {
    console.debug("⚠️ getCurrencyFromCity: City name is undefined or empty, defaulting to USD");
    return "USD";
  }
  
  // Handle airport codes (3-letter codes are likely airports)
  if (cityName && cityName.length === 3 && cityName === cityName.toUpperCase()) {
    console.debug("✈️ getCurrencyFromCity: City appears to be an airport code:", cityName);
    
    try {
      // Try to import the airport mapping
      const { getAirportCountry } = await import('@/utils/airportMapping');
      const country = await getAirportCountry(cityName);
      
      if (country) {
        console.debug(`✅ getCurrencyFromCity: Resolved airport ${cityName} to country ${country}`);
        const currency = countryCurrencyMap[country]?.currency || "USD";
        console.debug(`💰 getCurrencyFromCity: Currency for ${country} is ${currency}`);
        return currency;
      } else {
        console.debug(`⚠️ getCurrencyFromCity: Could not resolve country for airport ${cityName}, checking for hardcoded mappings`);
        
        // Add special cases for airports that might not be in the main mapping
        const specialAirportMappings: Record<string, string> = {
          'DPS': 'IDR', // Denpasar/Bali, Indonesia
        };
        
        if (specialAirportMappings[cityName]) {
          console.debug(`✅ getCurrencyFromCity: Found special mapping for ${cityName}: ${specialAirportMappings[cityName]}`);
          return specialAirportMappings[cityName];
        }
      }
    } catch (error) {
      console.error("⚠️ getCurrencyFromCity: Error resolving airport code:", error);
      console.debug("⚠️ getCurrencyFromCity: Full error:", JSON.stringify(error));
    }
  }
  
  try {
    // Check if the city exists in our city-to-country mapping
    console.debug("🔍 getCurrencyFromCity: Checking city in city-to-country mapping...");
    if (cityToCountryMap[cityName]) {
      const countryName = cityToCountryMap[cityName];
      console.debug(`✅ getCurrencyFromCity: Found city ${cityName} mapped to country ${countryName}`);
      const currency = countryCurrencyMap[countryName]?.currency || "USD";
      console.debug(`💰 getCurrencyFromCity: Currency for ${countryName} is ${currency}`);
      return currency;
    }
    
    // If not found in direct mapping, try to import countries data
    console.debug("📚 getCurrencyFromCity: Importing country data...");
    const { countries, getCountrySuggestions } = await import('@/utils/countryData');
    console.debug("📚 getCurrencyFromCity: Available countries:", countries.length);
    
    // Try to find if cityName matches directly with a country name
    for (const country of countries) {
      if (country.name.toLowerCase() === cityName.toLowerCase()) {
        console.debug(`✅ getCurrencyFromCity: City name ${cityName} matches country name`);
        const currency = countryCurrencyMap[country.name]?.currency || "USD";
        console.debug(`💰 getCurrencyFromCity: Currency for ${country.name} is ${currency}`);
        return currency;
      }
    }
    
    // If not found, default to USD
    console.debug(`⚠️ getCurrencyFromCity: Currency not found for city: ${cityName}, defaulting to USD`);
    return "USD";
  } catch (error) {
    console.error("❌ getCurrencyFromCity: Error determining currency from city:", error);
    return "USD"; // Default to USD on error
  }
};

// Function to get user's preferred currency from localStorage or profile
export const getUserPreferredCurrency = async (): Promise<string> => {
  console.debug("🔍 getUserPreferredCurrency: Starting currency preference lookup");
  
  // Check if we have the currency in localStorage
  const storedCurrency = localStorage.getItem('user_preferred_currency');
  console.debug("💾 getUserPreferredCurrency: Currency from localStorage:", storedCurrency);
  
  if (storedCurrency) {
    console.debug("✅ getUserPreferredCurrency: Using currency from localStorage:", storedCurrency);
    return storedCurrency;
  }
  
  try {
    // Try to get from Hey profile
    console.debug("👤 getUserPreferredCurrency: No localStorage currency, trying Hey profile...");
    const Hey = await import('@/lib/hey');
    const heyUtils = Hey.default.HeyUtils.getInstance();
    
    console.debug("🔐 getUserPreferredCurrency: Authentication status:", heyUtils.isUserAuthenticated());
    
    if (heyUtils.isUserAuthenticated()) {
      console.debug("📤 getUserPreferredCurrency: Fetching user profile from Hey...");
      const userProfile = await heyUtils.getUserProfile();
      console.debug("📥 getUserPreferredCurrency: User profile retrieved:", JSON.stringify(userProfile, null, 2));
      
      let currency: string;
      
      // Check if the user object has the nested structure or flat structure
      console.debug("🔍 getUserPreferredCurrency: Checking user profile structure...");
      if (userProfile?.user?.currency) {
        currency = userProfile.user.currency;
        console.debug("💰 getUserPreferredCurrency: Found currency in nested structure:", currency);
      } else if ((userProfile as any)?.currency) {
        currency = (userProfile as any).currency;
        console.debug("💰 getUserPreferredCurrency: Found currency in flat structure:", currency);
      } else {
        currency = "USD"; // Default
        console.debug("⚠️ getUserPreferredCurrency: No currency in profile, using default:", currency);
      }
      
      // Store in localStorage for future use
      try {
        localStorage.setItem('user_preferred_currency', currency);
        console.debug("💾 getUserPreferredCurrency: Storing currency in localStorage:", currency);
      } catch (error) {
        console.error("Error storing user currency in localStorage:", error);
        localStorage.clear();
      }
      return currency;
    } else {
      console.debug("⚠️ getUserPreferredCurrency: User not authenticated, using default USD");
    }
  } catch (error) {
    console.error("❌ getUserPreferredCurrency: Error fetching user currency:", error);
  }
  
  // Default to USD if all else fails
  console.debug("⚠️ getUserPreferredCurrency: Using default USD after all attempts failed");
  return "USD";
};
